import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, Shield, Smartphone } from "lucide-react";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { PageHeader } from "../components/storefront/PageHeader";
import { ResendVerificationEmail } from "../components/ResendVerificationEmail";
import { Button, Input } from "../components/ui";
import { maskPhone } from "../lib/phoneVerification";

export function AccountSettings() {
  const { user, refreshUser, fetchUserSecurity, userSecurity, config } = useAuth();
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [pw, setPw] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);
  const [totp, setTotp] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [pinForm, setPinForm] = useState({ password: "", pin: "", confirm: "" });

  useEffect(() => {
    void fetchUserSecurity();
  }, [fetchUserSecurity]);

  useEffect(() => {
    setDisplayName(user?.displayName ?? "");
  }, [user?.displayName]);

  async function saveProfile() {
    setBusy(true);
    try {
      await api("/auth/profile", { method: "PATCH", body: { displayName } });
      await refreshUser();
      toast("success", "Profile updated");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  }

  async function changePassword() {
    if (!pw.current || pw.next.length < 8) {
      toast("error", "New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api("/auth/change-password", { body: { currentPassword: pw.current, newPassword: pw.next } });
      setPw({ current: "", next: "" });
      toast("success", "Password changed");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't change password");
    } finally {
      setBusy(false);
    }
  }

  async function toggleEmailMfa(enabled: boolean) {
    setBusy(true);
    try {
      await api(enabled ? "/auth/mfa/email/enable" : "/auth/mfa/email/disable", { body: {} });
      await fetchUserSecurity();
      toast("success", enabled ? "Email 2FA enabled" : "Email 2FA disabled");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update 2FA");
    } finally {
      setBusy(false);
    }
  }

  async function startTotp() {
    setBusy(true);
    try {
      const data = await api<{ secret: string; otpauthUrl: string }>("/auth/mfa/totp/enroll", { body: {} });
      setTotp(data);
      setTotpCode("");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't start enrollment");
    } finally {
      setBusy(false);
    }
  }

  async function confirmTotp() {
    setBusy(true);
    try {
      await api("/auth/mfa/totp/confirm", { body: { code: totpCode.trim() } });
      setTotp(null);
      await fetchUserSecurity();
      toast("success", "Authenticator 2FA enabled");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  async function disableTotp() {
    setBusy(true);
    try {
      await api("/auth/mfa/totp/disable", { body: {} });
      await fetchUserSecurity();
      toast("success", "Authenticator 2FA disabled");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't disable 2FA");
    } finally {
      setBusy(false);
    }
  }

  async function setPin(e: React.FormEvent) {
    e.preventDefault();
    if (pinForm.pin.length !== 6 || pinForm.pin !== pinForm.confirm) {
      toast("error", "PIN must be 6 matching digits");
      return;
    }
    setBusy(true);
    try {
      await api("/auth/pin/set", { body: { password: pinForm.password, pin: pinForm.pin } });
      setPinForm({ password: "", pin: "", confirm: "" });
      await fetchUserSecurity();
      toast("success", "PIN saved");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't save PIN");
    } finally {
      setBusy(false);
    }
  }

  if (!userSecurity) {
    return <p className="px-4 py-8 text-center text-xs text-white/50">Loading…</p>;
  }

  return (
    <div className="space-y-4 px-4 pt-4 pb-8 lg:px-6">
      <Link to="/account" className="text-xs font-medium text-brand-pink">
        ← Account
      </Link>
      <PageHeader
        title="Settings"
        description="Update your profile, password, two-factor auth, and checkout PIN."
      />

      <ResendVerificationEmail />

      <section className="glass-panel sidebar-form space-y-0 rounded-2xl p-4">
        <p className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-white/40">Profile</p>
        <Input label="Display name" placeholder="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        <div className="form-actions-footer pt-2">
          <Button variant="outline" className="w-full" disabled={busy} onClick={() => void saveProfile()}>
            Save
          </Button>
        </div>
      </section>

      {config?.securityPolicy?.smsOtpEnabled && (
        <section className="glass-panel sidebar-form space-y-0 rounded-2xl p-4">
          <p className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            Mobile number
          </p>
          {user?.phoneVerifiedAt ? (
            <p className="text-sm text-white/80">{maskPhone(user.phone)}</p>
          ) : (
            <p className="text-sm text-white/60">
              Not verified.{" "}
              <Link to="/account/verify-phone" className="font-semibold text-brand-pink hover:underline">
                Verify now
              </Link>
            </p>
          )}
        </section>
      )}

      <section className="glass-panel sidebar-form space-y-0 rounded-2xl p-4">
        <p className="mb-3 font-display text-xs font-semibold uppercase tracking-widest text-white/40">Password</p>
        <Input
          type="password"
          label="Current password"
          placeholder="Current password"
          value={pw.current}
          onChange={(e) => setPw({ ...pw, current: e.target.value })}
        />
        <Input
          type="password"
          label="New password"
          placeholder="New password (min 8 chars)"
          value={pw.next}
          onChange={(e) => setPw({ ...pw, next: e.target.value })}
        />
        <div className="form-actions-footer pt-2">
          <Button variant="outline" className="w-full" disabled={busy} onClick={() => void changePassword()}>
            Change
          </Button>
        </div>
      </section>

      {(userSecurity.mfaEmailAllowed || userSecurity.mfaTotpAllowed) && (
        <section className="glass-panel rounded-2xl p-4">
          <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            Two-factor authentication
          </h2>
          {userSecurity.mfaEmailAllowed && (
            <label className="mt-3 flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={userSecurity.mfaEmailEnabled}
                disabled={busy}
                onChange={(e) => void toggleEmailMfa(e.target.checked)}
              />
              Email verification codes
            </label>
          )}
          {userSecurity.mfaTotpAllowed && (
            <div className="mt-3">
              {userSecurity.totpEnabled ? (
                <Button variant="ghost" disabled={busy} onClick={() => void disableTotp()}>
                  Disable authenticator
                </Button>
              ) : totp ? (
                <div className="sidebar-form space-y-0">
                  <code className="mb-2 block break-all rounded bg-white/5 p-2 text-[10px]">{totp.secret}</code>
                  <Input
                    label="Authenticator code"
                    placeholder="Authenticator code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                  />
                  <Button disabled={busy} onClick={() => void confirmTotp()}>
                    Confirm
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void startTotp()}
                  className="flex items-center gap-1.5 text-xs text-brand-pink"
                >
                  <Smartphone size={14} />
                  Set up authenticator
                </button>
              )}
            </div>
          )}
        </section>
      )}

      {userSecurity.pinEnabled && (
        <section className="glass-panel rounded-2xl p-4">
          <h2 className="flex items-center gap-1.5 font-display text-xs font-semibold uppercase tracking-widest text-white/40">
            <Shield size={14} />
            PIN login
          </h2>
          {userSecurity.pinSet && <p className="mt-1 text-xs text-emerald-400/90">PIN is set</p>}
          <form className="sidebar-form mt-3 space-y-0" onSubmit={setPin}>
            <Input
              type="password"
              label="Current password"
              placeholder="Current password"
              autoComplete="current-password"
              value={pinForm.password}
              onChange={(e) => setPinForm((p) => ({ ...p, password: e.target.value }))}
            />
            <Input
              type="password"
              inputMode="numeric"
              maxLength={6}
              label="New PIN"
              placeholder="New PIN (6 digits)"
              value={pinForm.pin}
              onChange={(e) => setPinForm((p) => ({ ...p, pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
            <Input
              type="password"
              inputMode="numeric"
              maxLength={6}
              label="Confirm PIN"
              placeholder="Confirm PIN"
              value={pinForm.confirm}
              onChange={(e) => setPinForm((p) => ({ ...p, confirm: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
            />
            <div className="form-actions-footer pt-2">
              <Button type="submit" disabled={busy}>
                Save
              </Button>
            </div>
          </form>
        </section>
      )}
    </div>
  );
}

/** @deprecated Use AccountSettings — kept for route alias imports */
export const AccountSecurity = AccountSettings;
